import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor,
  updatePassword,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  getMultiFactorResolver,
} from 'firebase/auth';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import { auth } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import { getRmsProviderRoleLabel } from '../../utils/providerRoles';
import MfaDialog from '../../../components/auth/MfaDialog';
import PasswordRequirements from '../../../components/auth/PasswordRequirements';
import { validatePasswordString } from '../../../hooks/auth/usePasswordValidation';
import InfoTooltip from '../../../components/InfoTooltip';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';

/**
 * RmsProviderSettings
 * -------------------
 * Account settings for RMS provider users, mirroring the Assessment platform's
 * ProviderSettings "Account" section (TD-33):
 *   - Read-only account summary (name, email, role, organisation).
 *   - Change Password — reauthenticate, then updatePassword; if the account has
 *     MFA enrolled, complete the SMS challenge via the shared MfaDialog first.
 *   - Two-factor authentication — show enrolment status + a link to manage it.
 *
 * The Assessment "Organisation Settings" section (clinical instruments/scales,
 * assessment-letter signatures, branding) is product-specific and not applicable
 * to RMS, so it is intentionally omitted.
 */
export default function RmsProviderSettings() {
  const navigate = useNavigate();
  const { user, claims } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // MFA reauth challenge state (mirrors the Assessment settings flow).
  const [showMFAPrompt, setShowMFAPrompt] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const [resolver, setResolver] = useState(null);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [mfaSentAt, setMfaSentAt] = useState(0);
  const [mfaBusy, setMfaBusy] = useState(false);
  const recaptchaVerifierRef = useRef(null);

  const mfaEnrolled = user ? (multiFactor(user).enrolledFactors || []).length > 0 : false;

  const onSuccess = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setSuccess('Password successfully updated. Please sign in again.');
    setTimeout(async () => {
      try { await signOut(auth); } catch (_) {}
      navigate('/rms/provider-sign-in', { replace: true });
    }, 2000);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      const u = auth.currentUser;
      if (!u) throw new Error('Please sign in first');

      const { errors } = validatePasswordString(newPassword);
      if (errors.length > 0) throw new Error(errors.join('. '));
      if (newPassword !== confirmPassword) throw new Error('New passwords do not match');
      if (newPassword === currentPassword) throw new Error('New password must be different from your current password');

      const credential = EmailAuthProvider.credential(u.email, currentPassword);
      try {
        await reauthenticateWithCredential(u, credential);
        await updatePassword(u, newPassword);
        onSuccess();
      } catch (err) {
        if (err.code === 'auth/multi-factor-auth-required') {
          const res = getMultiFactorResolver(auth, err);
          if (!recaptchaVerifierRef.current) {
            recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'rms-recaptcha-container', { size: 'invisible' });
          }
          const phoneHint = res.hints[0];
          const phoneAuthProvider = new PhoneAuthProvider(auth);
          const vId = await phoneAuthProvider.verifyPhoneNumber(
            { multiFactorHint: phoneHint, session: res.session },
            recaptchaVerifierRef.current
          );
          setVerificationId(vId);
          setResolver(res);
          setMfaSentAt(Date.now());
          try {
            const last4 = (phoneHint?.phoneNumber || '').replace(/\D/g, '').slice(-4);
            setMaskedPhone(last4 ? `••••${last4}` : '');
          } catch (_) {}
          setIsLoading(false);
          setShowMFAPrompt(true);
          return;
        }
        if (err.code === 'auth/wrong-password') {
          setError('Current password is incorrect');
          setCurrentPassword('');
        } else {
          throw err;
        }
      }
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setError('Please sign in again before changing your password');
        try { await signOut(auth); } catch (_) {}
        navigate('/rms/provider-sign-in', { replace: true });
      } else {
        setError(err?.message || 'Error changing password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const eyeButton = (shown, toggle) => (
    <button
      type="button"
      onClick={toggle}
      className="absolute inset-y-0 right-0 pr-3 flex items-center"
      aria-label={shown ? 'Hide password' : 'Show password'}
    >
      {shown ? <EyeOffIcon className="h-5 w-5 text-gray-400" /> : <EyeIcon className="h-5 w-5 text-gray-400" />}
    </button>
  );

  const fullName = [user?.displayName].filter(Boolean).join(' ') || '—';

  return (
    <div className="min-h-screen bg-gray-50">
      <RmsTopNavBar breadcrumbs={[{ label: 'Home', path: '/rms/provider/dashboard' }, { label: 'Settings', path: null }]} />
      <div id="rms-recaptcha-container" />

      <div className="flex flex-col items-center gap-4 p-8">
        <h2 className="text-2xl font-bold">Settings</h2>

        {/* Account summary */}
        <div className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Account</h3>
          <dl className="grid grid-cols-3 gap-y-3 text-sm">
            <dt className="text-gray-500">Name</dt><dd className="col-span-2 text-gray-900">{fullName}</dd>
            <dt className="text-gray-500">Email</dt><dd className="col-span-2 text-gray-900">{user?.email || '—'}</dd>
            <dt className="text-gray-500">Role</dt><dd className="col-span-2 text-gray-900">{getRmsProviderRoleLabel(claims)}</dd>
            <dt className="text-gray-500">Organisation</dt><dd className="col-span-2 text-gray-900">{claims?.rmsOrgId || '—'}</dd>
          </dl>
        </div>

        {/* Change password */}
        <div className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Change Password</h3>
          {success && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">{success}</div>}
          {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>}
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => { setCurrentPassword(e.target.value); setError(''); }}
                  required
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                />
                {eyeButton(showCurrent, () => setShowCurrent(!showCurrent))}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <InfoTooltip side="right" content="Your password needs 12+ characters and at least 1 number, 1 symbol, and 1 upper & lower case letter" />
              </div>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                  required
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                />
                {eyeButton(showNew, () => setShowNew(!showNew))}
              </div>
              <PasswordRequirements value={newPassword} onValidityChange={setIsPasswordValid} className="mt-2" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  required
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                />
                {eyeButton(showConfirm, () => setShowConfirm(!showConfirm))}
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !isPasswordValid || !currentPassword || !confirmPassword}
              className="w-full py-2.5 px-4 rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        </div>

        {/* Two-factor authentication */}
        <div className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-2">Two-Factor Authentication</h3>
          <p className="text-sm text-gray-600 mb-4">
            {mfaEnrolled
              ? 'Two-factor authentication is enabled on your account.'
              : 'Two-factor authentication is not yet set up.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/set-up-mfa')}
            className="px-4 py-2 text-sm font-medium rounded-lg text-primary bg-white border border-primary/30 hover:bg-primary/5"
          >
            {mfaEnrolled ? 'Manage two-factor authentication' : 'Set up two-factor authentication'}
          </button>
        </div>
      </div>

      <MfaDialog
        open={showMFAPrompt}
        mfaType="phone"
        onOpenChange={(open) => {
          setShowMFAPrompt(open);
          if (!open && recaptchaVerifierRef.current) {
            try { recaptchaVerifierRef.current.clear(); } catch (_) {}
            recaptchaVerifierRef.current = null;
          }
        }}
        maskedPhone={maskedPhone}
        isLoading={mfaBusy}
        error={error}
        sentAt={mfaSentAt}
        cooldownSeconds={30}
        tooltipSide="right"
        onVerify={async (code) => {
          setError('');
          try {
            setMfaBusy(true);
            const cred = PhoneAuthProvider.credential(verificationId, code);
            const assertion = PhoneMultiFactorGenerator.assertion(cred);
            await resolver.resolveSignIn(assertion);
            await updatePassword(auth.currentUser, newPassword);
            setShowMFAPrompt(false);
            onSuccess();
          } catch (err) {
            // Distinguish the common failure modes rather than always blaming the
            // code, so a rate-limited / expired / network error isn't misreported.
            if (err?.code === 'auth/invalid-verification-code' || err?.code === 'auth/missing-verification-code') {
              setError('Invalid verification code. Please try again.');
            } else if (err?.code === 'auth/code-expired') {
              setError('That code has expired. Request a new one.');
            } else if (err?.code === 'auth/too-many-requests') {
              setError('Too many attempts. Please wait a moment and try again.');
            } else {
              console.error('[RmsProviderSettings] MFA verification failed:', err);
              setError('Could not verify the code. Please try again.');
            }
          } finally {
            setMfaBusy(false);
          }
        }}
        onCancel={() => setShowMFAPrompt(false)}
      />
    </div>
  );
}
